type StorageAction = "get" | "set" | "remove";

export const createSafeLocalStorage = (): Storage => {
  const memoryStorage = new Map<string, string>();

  const getLocalStorage = () => {
    if (typeof window === "undefined") return null;
    try {
      return window.localStorage;
    } catch {
      return null;
    }
  };

  return {
    get length() {
      const local = getLocalStorage();
      return local ? local.length : memoryStorage.size;
    },
    clear: () => {
      const local = getLocalStorage();
      if (local) {
        local.clear();
        return;
      }
      memoryStorage.clear();
    },
    getItem: (key: string) => {
      const local = getLocalStorage();
      if (local) {
        try {
          return local.getItem(key);
        } catch {
          return memoryStorage.get(key) ?? null;
        }
      }
      return memoryStorage.get(key) ?? null;
    },
    key: (index: number) => {
      const local = getLocalStorage();
      if (local) {
        try {
          return local.key(index);
        } catch {
          return Array.from(memoryStorage.keys())[index] ?? null;
        }
      }
      return Array.from(memoryStorage.keys())[index] ?? null;
    },
    removeItem: (key: string) => {
      const local = getLocalStorage();
      if (local) {
        try {
          local.removeItem(key);
          return;
        } catch {
          memoryStorage.delete(key);
          return;
        }
      }
      memoryStorage.delete(key);
    },
    setItem: (key: string, value: string) => {
      const local = getLocalStorage();
      if (local) {
        try {
          local.setItem(key, value);
          return;
        } catch {
          memoryStorage.set(key, value);
          return;
        }
      }
      memoryStorage.set(key, value);
    },
  } as Storage;
};

const safeLocalStorage = createSafeLocalStorage();
const safeSessionStorage = (() => {
  const memoryStorage = new Map<string, string>();

  const getSessionStorage = () => {
    if (typeof window === "undefined") return null;
    try {
      return window.sessionStorage;
    } catch {
      return null;
    }
  };

  return {
    get length() {
      const session = getSessionStorage();
      return session ? session.length : memoryStorage.size;
    },
    clear: () => {
      const session = getSessionStorage();
      if (session) {
        session.clear();
        return;
      }
      memoryStorage.clear();
    },
    getItem: (key: string) => {
      const session = getSessionStorage();
      if (session) {
        try {
          return session.getItem(key);
        } catch {
          return memoryStorage.get(key) ?? null;
        }
      }
      return memoryStorage.get(key) ?? null;
    },
    key: (index: number) => {
      const session = getSessionStorage();
      if (session) {
        try {
          return session.key(index);
        } catch {
          return Array.from(memoryStorage.keys())[index] ?? null;
        }
      }
      return Array.from(memoryStorage.keys())[index] ?? null;
    },
    removeItem: (key: string) => {
      const session = getSessionStorage();
      if (session) {
        try {
          session.removeItem(key);
          return;
        } catch {
          memoryStorage.delete(key);
          return;
        }
      }
      memoryStorage.delete(key);
    },
    setItem: (key: string, value: string) => {
      const session = getSessionStorage();
      if (session) {
        try {
          session.setItem(key, value);
          return;
        } catch {
          memoryStorage.set(key, value);
          return;
        }
      }
      memoryStorage.set(key, value);
    },
  } as Storage;
})();

const safeStorageAction = <T>(
  action: StorageAction,
  key: string,
  value?: string
): T | null => {
  try {
    switch (action) {
      case "get":
        return sessionStorage.getItem(key) as T | null;
      case "set":
        sessionStorage.setItem(key, value ?? "");
        return true as T;
      case "remove":
        sessionStorage.removeItem(key);
        return true as T;
      default:
        return null;
    }
  } catch {
    return null;
  }
};

export const getSessionItem = (key: string): string | null =>
  safeStorageAction<string>("get", key);

export const setSessionItem = (key: string, value: string): boolean =>
  !!safeStorageAction<boolean>("set", key, value);

export const removeSessionItem = (key: string): boolean =>
  !!safeStorageAction<boolean>("remove", key);

export const getLocalItem = (key: string): string | null =>
  safeLocalStorage.getItem(key);

export const setLocalItem = (key: string, value: string): void =>
  safeLocalStorage.setItem(key, value);

export const removeLocalItem = (key: string): void =>
  safeLocalStorage.removeItem(key);

export const getSafeSessionStorage = (): Storage => safeSessionStorage;

export const getSafeLocalStorage = (): Storage => safeLocalStorage;

export const safeJsonParse = <T>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};
